import React, { useState, useEffect, useCallback } from "react";
import { 
  doc, 
  setDoc, 
  collection, 
  onSnapshot,
  serverTimestamp,
  getDocs,
  writeBatch
} from "firebase/firestore";
import { db } from "../../../../../../firebase/config.js";
import { incomeTaskTemplateNorway } from "../template/incomeTaskTemplateNorway.js";
import Header from "../../../../../../components/Header/Header.js";
import RightPanleIncomeTeam from "../right-panel/RightPanelIncomeTeam.js"; 
import "./incomeTeamNorway.css";
import incomeFlag from '../../../../../../assets/Countries/norway.png';
import TitleButton from "../../../../../../components/TitleButton/TitleButton.js";
// Constants for shared default objects
const EMPTY_CHECK = { checked: false, timestamp: null, user: null };
const EMPTY_FIELD = { value: "", active: true };

// Helper function to normalize allocations (defensive fix for legacy data)
const normalizeAllocations = (allocations) => {
  if (!allocations) return [];

  // 🔴 convert object → array (legacy data)
  if (!Array.isArray(allocations)) {
    return [
      {
        id: crypto.randomUUID(),
        ...allocations
      }
    ];
  }

  return allocations;
};

// Fixed transform function - allocations fields should be objects
const transformFirestoreTasks = (firestoreTasks) => {
  return firestoreTasks.map(task => ({
    id: task.id,

    Time: task.Time || "",
    Task: task.Task || "",

    taskPrimary: task.taskPrimary || EMPTY_FIELD,
    taskPrimaryCheck: task.taskPrimaryCheck || EMPTY_CHECK,

    taskDuality: task.taskDuality || EMPTY_FIELD,
    taskDualityCheck: task.taskDualityCheck || EMPTY_CHECK,

    Items: task.Items || "",
    Comments: task.Comments || "",

    // ✅ IMPORTANT: preserve config
    allocationFields: task.allocationFields || {
      shortName: true,
      isin: true,
      corpRef: true,
      ack: true
    },

    actions: task.actions || {
      canAdd: true
    },

    allocations: normalizeAllocations(task.allocations).map(a => ({
      id: a.id || crypto.randomUUID(),
      allocPrimary: a.allocPrimary || EMPTY_FIELD,
      allocPrimaryCheck: a.allocPrimaryCheck || EMPTY_CHECK,
      allocDuality: a.allocDuality || EMPTY_FIELD,
      allocDualityCheck: a.allocDualityCheck || EMPTY_CHECK,
      shortName: a.shortName || EMPTY_FIELD,
      isin: a.isin || EMPTY_FIELD,
      corpRef: a.corpRef || EMPTY_FIELD,
      ack: a.ack || EMPTY_FIELD
    }))
  }));
};


const getMonth = (date) => date.getMonth();

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => date.toISOString().split('T')[0];

// Firestore subscription helper
const subscribeToIncomeTasks = (country, date, callback) => {
  if (!country || !date) {
    console.warn("Missing country or date for subscription");
    return () => {};
  }

  try {
    // Subscribe to tasks for specific country and date
    const tasksRef = collection(db, "incomeTeams", country, "dates", date, "tasks");
    
    // Get real-time updates
    const unsubscribe = onSnapshot(tasksRef, (snapshot) => {
      const tasks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(tasks);
    }, (error) => {
      console.error("Error subscribing to tasks:", error);
    });

    return unsubscribe;
  } catch (error) {
    console.error("Error setting up subscription:", error);
    return () => {};
  }
};

// Fetch checklists (dates) from Firestore
const fetchChecklistsFromFirestore = async (country) => {
  try {
    const datesRef = collection(db, "incomeTeams", country, "dates");
    const snapshot = await getDocs(datesRef);
    
    const checklists = [];
    snapshot.forEach(doc => {
      const dateStr = doc.id; // Document ID is the date (YYYY-MM-DD)
      const dateObj = new Date(dateStr);
      const data = doc.data();
      
      checklists.push({
        id: doc.id,
        date: dateObj,
        label: dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        formattedDate: dateStr,
        ...data
      });
    });
    
    // Sort by date (newest first)
    return checklists.sort((a, b) => b.date - a.date);
  } catch (error) {
    console.error("Error fetching checklists:", error);
    return [];
  }
};

// Helper function to check if tasks already exist
const checkIfTasksExist = async (country, date) => {
  try {
    const tasksRef = collection(db, "incomeTeams", country, "dates", date, "tasks");
    const snapshot = await getDocs(tasksRef);
    return !snapshot.empty;
  } catch (error) {
    console.error("Error checking tasks:", error);
    return false;
  }
};

// Seed tasks from template
const seedTasksFromTemplate = async (country, date, template, user) => {
  try {
    // Check if tasks already exist
    const tasksExist = await checkIfTasksExist(country, date);
    if (tasksExist) {
      console.log(`Tasks already exist for ${date}, skipping seeding`);
      return;
    }

    const batch = writeBatch(db);
    const tasksRef = collection(db, "incomeTeams", country, "dates", date, "tasks");
    
    // Transform template to match Firestore structure
    template.forEach((taskTemplate) => {
      // Create a task document reference with auto-generated ID
      const taskDocRef = doc(tasksRef);
      
      // Prepare task data with user info and timestamps
      const taskData = {
        ...taskTemplate,
        // Ensure allocations have the correct structure
        allocations: taskTemplate.allocations?.map(alloc => ({
          ...alloc,
          shortName: alloc.shortName || EMPTY_FIELD,
          isin: alloc.isin || EMPTY_FIELD,
          corpRef: alloc.corpRef || EMPTY_FIELD,
          ack: alloc.ack || EMPTY_FIELD
        })) || [],
        createdAt: serverTimestamp(),
        createdBy: user?.name || "system",
        lastModified: serverTimestamp(),
        lastModifiedBy: user?.name || "system",
        status: "active"
      };
      
      batch.set(taskDocRef, taskData);
    });
    
    await batch.commit();
    console.log(`Successfully seeded ${template.length} tasks for ${date}`);
  } catch (error) {
    console.error("Error seeding tasks:", error);
    throw error;
  }
};

export default function IncomeTeam({ user }) {
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingChecklist, setCreatingChecklist] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [inputDate, setInputDate] = useState("");
  const [error, setError] = useState("");

  // NEW STATE: Years + Panel Collapse
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const years = [2025, 2026, 2027];
  const [openYear, setOpenYear] = useState(null);
  const [openMonth, setOpenMonth] = useState(null);

  // Country - hardcoded for now, can be made dynamic
  const country = "norway";

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const migrateLocalStorageToFirestore = useCallback(
    async (localChecklists, country) => {
      for (const checklist of localChecklists) {
        const formattedDate = formatDate(new Date(checklist.date));

        try {
          await setDoc(
            doc(db, "incomeTeams", country, "dates", formattedDate),
            {
              createdAt: serverTimestamp(),
              createdBy: user?.name || "system",
              migrated: true
            }
          );

          const tasksKey = `checklist_${formattedDate}`;
          const localTasks = JSON.parse(localStorage.getItem(tasksKey) || "[]");

          if (localTasks.length > 0) {
            const batch = writeBatch(db);
            const tasksRef = collection(
              db,
              "incomeTeams",
              country,
              "dates",
              formattedDate,
              "tasks"
            );

            for (const task of localTasks) {
              const taskDocRef = doc(tasksRef);
              batch.set(taskDocRef, {
                ...task,
                allocations: normalizeAllocations(task.allocations).map(alloc => ({
                  ...alloc,
                  shortName: alloc.shortName || EMPTY_FIELD,
                  isin: alloc.isin || EMPTY_FIELD,
                  corpRef: alloc.corpRef || EMPTY_FIELD,
                  ack: alloc.ack || EMPTY_FIELD
                })),
                migrated: true,
                migratedAt: serverTimestamp(),
                createdAt: serverTimestamp()
              });
            }

            await batch.commit();
            localStorage.removeItem(tasksKey);
          } else {
            await seedTasksFromTemplate(
              country,
              formattedDate,
              incomeTaskTemplateNorway,
              user
            );
          }
        } catch (error) {
          console.error(`Error migrating checklist ${formattedDate}:`, error);
        }
      }
    },
    [user]
  );

  // Load checklists from Firestore on component mount
  useEffect(() => {
    const loadChecklists = async () => {
      setLoading(true);
      try {
        const firestoreChecklists = await fetchChecklistsFromFirestore(country);
        setChecklists(firestoreChecklists);
        
        // Also check localStorage for backward compatibility (migration)
        const localStorageChecklists = JSON.parse(localStorage.getItem('checklists') || '[]');
        if (localStorageChecklists.length > 0) {
          // Migrate old localStorage checklists to Firestore
          await migrateLocalStorageToFirestore(localStorageChecklists, country);
          localStorage.removeItem('checklists'); // Clean up after migration
        }
      } catch (error) {
        console.error("Error loading checklists:", error);
      } finally {
        setLoading(false);
      }
    };

    loadChecklists();
  }, [country, migrateLocalStorageToFirestore]);

  // Subscribe to tasks when a checklist is selected
  useEffect(() => {
    if (!selectedChecklist || !selectedChecklist.formattedDate) return;
    
    const unsubscribe = subscribeToIncomeTasks(
      country,
      selectedChecklist.formattedDate,
      (fetchedTasks) => {
        setTasks(transformFirestoreTasks(fetchedTasks));
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [country, selectedChecklist]);

  // OPEN POPUP
  const openPopup = () => {
    setInputDate("");
    setError("");
    setShowPopup(true);
  };

  // UPDATED SAVE CHECKLIST - Creates date document AND seeds template tasks
  const saveChecklist = async () => {
    if (!inputDate) {
      setError("Please select date.");
      return;
    }

    const dateObj = new Date(inputDate);
    const formattedDate = formatDate(dateObj);
    
    // Check if date already exists in local state
    const existsInState = checklists.some(item => item.formattedDate === formattedDate);
    
    if (existsInState) {
      setError("Checklist with this date already exists!");
      return;
    }

    // Check if date exists in Firestore
    try {
      const dateSnapshot = await getDocs(collection(db, "incomeTeams", country, "dates"));
      const dateExists = dateSnapshot.docs.some(doc => doc.id === formattedDate);
      
      if (dateExists) {
        setError("Checklist with this date already exists in the database!");
        return;
      }
    } catch (error) {
      console.error("Error checking date existence:", error);
    }

    try {
      setCreatingChecklist(true);
      
      // 1️⃣ Create DATE document in Firestore
      const dateRef = doc(db, "incomeTeams", country, "dates", formattedDate);
      await setDoc(dateRef, {
        createdAt: serverTimestamp(),
        createdBy: user?.name || "anonymous",
        team: "income",
        country: country,
        status: "active",
        taskCount: incomeTaskTemplateNorway.length
      });

      // 2️⃣ Seed TASKS from template
      await seedTasksFromTemplate(country, formattedDate, incomeTaskTemplateNorway, user);

      // 3️⃣ Update UI
      const newChecklist = {
        id: formattedDate,
        date: dateObj,
        label: dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }),
        formattedDate: formattedDate
      };

      setChecklists(prev => [...prev, newChecklist]);
      setSelectedChecklist(newChecklist); // Auto-select the new checklist
      setShowPopup(false);
      
      console.log(`Successfully created checklist for ${formattedDate} with ${incomeTaskTemplateNorway.length} tasks`);
      
    } catch (error) {
      console.error("Error creating checklist:", error);
      setError("Failed to create checklist. Please try again.");
    } finally {
      setCreatingChecklist(false);
    }
  };

  // Prepare data for RightPanleIncomeTeam
  const checklistData = selectedChecklist ? {
    ...selectedChecklist,
    // Pass Firestore tasks to the component
    tasks: tasks,
    // Pass country and date for task updates
    country: country,
    date: selectedChecklist.formattedDate,
    // Pass user info for audit logs
    user: user
  } : null;

  return (
    <div className="income-team-container">
      <Header user={user} />
      <TitleButton
        title="Income Team - Norway"
        flagSrc={incomeFlag}
        checklist={selectedChecklist}
        tasks={tasks}
      />

      {(loading || creatingChecklist) && (
        <div className="loading-indicator">
          {creatingChecklist ? "Creating checklist with tasks..." : "Loading..."}
        </div>
      )}

      <div className="income-team-body">
        {/* LEFT COLUMN */}
        <div className={`left-column ${isPanelCollapsed ? "collapsed" : ""}`}>
          {!isPanelCollapsed && (
            <>
              <button 
                className="checklit-add-btn" 
                onClick={openPopup}
                disabled={loading || creatingChecklist}
              >
                + Checklist
              </button>

              {years.map((year) => (
                <div key={year} className="year-block">
                  {/* YEAR TITLE */}
                  <div
                    className="year-title"
                    onClick={() => setOpenYear(openYear === year ? null : year)}
                  >
                    {year}
                    <span className="arrow">
                      {openYear === year ? "▲" : "▼"}
                    </span>
                  </div>

                  {/* MONTHS */}
                  {openYear === year && months.map((month, mIndex) => (
                    <div key={mIndex} className="month-block">
                      <div
                        className="month-title"
                        onClick={() =>
                          setOpenMonth(
                            openMonth === `${year}-${mIndex}` ? null : `${year}-${mIndex}`
                          )
                        }
                      >
                        {month}
                        <span className="arrow">
                          {openMonth === `${year}-${mIndex}` ? "▲" : "▼"}
                        </span>
                      </div>

                      {/* DATES */}
                      {openMonth === `${year}-${mIndex}` && (
                        <div className="month-content">
                          {checklists
                            .filter(
                              c =>
                                c.date.getFullYear() === year &&
                                getMonth(c.date) === mIndex
                            )
                            .map(item => (
                              <div
                                key={item.id}
                                className={`date-item ${
                                  selectedChecklist?.id === item.id ? "active" : ""
                                }`}
                                onClick={() => setSelectedChecklist(item)}
                              >
                                {item.label}
                              </div>
                            ))}

                          {checklists.filter(
                            c =>
                              c.date.getFullYear() === year &&
                              getMonth(c.date) === mIndex
                          ).length === 0 && (
                            <div className="empty">No checklists</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        
        {/* COLLAPSE BUTTON - MOVED OUTSIDE LEFT COLUMN */}
        <button
          className="collapse-btn"
          onClick={() => setIsPanelCollapsed(prev => !prev)}
        >
          {isPanelCollapsed ? "▶" : "◀"}
        </button>

        {/* RIGHT COLUMN */}
        <div className="right-column">
          {!selectedChecklist && (
            <div className="placeholder">
              {checklists.length === 0 ? 
                "Click '+ Checklist' to create your first checklist" : 
                "Select a date to see information"}
            </div>
          )}

          {selectedChecklist && checklistData && (
            <RightPanleIncomeTeam data={checklistData} />
          )}
        </div>
      </div>

      {/* POPUP WINDOW */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h2>Create Checklist</h2>
            <p className="popup-subtitle">
              This will create a date with {incomeTaskTemplateNorway.length} pre-configured tasks
            </p>

            <input
              type="date"
              value={inputDate}
              onChange={e => setInputDate(e.target.value)}
              disabled={creatingChecklist}
            />

            {error && <p className="error">{error}</p>}

            <div className="popup-buttons">
              <button 
                onClick={saveChecklist}
                disabled={creatingChecklist || !inputDate}
              >
                {creatingChecklist ? "Creating..." : "Create Checklist"}
              </button>
              <button 
                onClick={() => setShowPopup(false)}
                disabled={creatingChecklist}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}